import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import CloseButton from './shared/CloseButton';
import { useMapOnboarding } from '../hooks/useMapOnboarding';
import GoogleMapOverlay from './GoogleMapOverlay';
import SectionAnalysisPopup from './SectionAnalysisPopup';
import TimeLostLegend from './TimeLostLegend';
import OptimalTimePopup from './OptimalTimePopup';
import AnimationProgressSlider from './AnimationProgressSlider';
import PlaybackSpeedSelector from './PlaybackSpeedSelector';
import { useAnalysisCache } from '../context/AnalysisCacheContext';
import { formatTime, formatTimeDifference, getDeltaBestLapColor, getDeltaOptimalColor } from '../utils/timeUtils';
import { getColorForTimeLost, getPathSegmentIndices } from '../utils/mapUtils';
import { calculateSectionTiming, getSmoothAnimationData } from '../utils/animationUtils';
import { VISUAL_UPDATE_INTERVAL_MS } from '../constants/animation';

// TypeScript interfaces
interface LapData {
  lap_number: number;
  latitude: number[];
  longitude: number[];
  displacement: number[];
  lap_time: number;
  optimal_lap_time: number | null;
  time: number[];
  lat_accel: number[];
  long_accel: number[];
  optimal_lat_accel: number[] | null;
  optimal_long_accel: number[] | null;
  speed: number[];
  optimal_speed: number[] | null;
  optimal_time: number[] | null;
  time_lost: number[] | null;
  speed_delta: number[] | null;
  best_lap_time: number | null;
  filename: string;
  analysis?: {
    section_analysis?: SectionAnalysis[];
  };
}

interface SectionAnalysis {
  start: number;
  end: number;
  time_lost: number;
  description: string;
}

interface MapDimensions {
  width: number;
  height: number;
}

// Constants
const SPEED_COLORS = {
  ACTUAL: '#ef4444', // Red for actual speed
  OPTIMAL: '#10b981'  // Green for optimal speed
};

const DEFAULT_MAP_DIMENSIONS = { width: 800, height: 600 };
const FALLBACK_GPS_BOUNDS = {
  SW: { lat: 37.4, lng: -122.1 },
  NE: { lat: 37.5, lng: -122.0 }
};
const VIEWPORT_FILL_RATIOS = {
  PORTRAIT: 0.95,
  LANDSCAPE: 0.9
};
const MIN_GPS_PADDING = 0.0002; // ~20 meters minimum padding
const INITIAL_ANIMATION_DELAY = 1000; // 1 second
const DEFAULT_CLICK_X = 400; // Default center position

const LapVisualizationPage: React.FC = () => {
  const { sessionId, lapNumber } = useParams<{ sessionId: string; lapNumber: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAnalyzedLap = (location.state as any)?.analyzed ?? true;
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const authLoading = !isLoaded;
  const isAuthenticated = isSignedIn;
  const { getCachedAnalysis, setCachedAnalysis } = useAnalysisCache();
  const [lapData, setLapData] = useState<LapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clickedSection, setClickedSection] = useState<SectionAnalysis | null>(null);
  const [sessionStatus, setSessionStatus] = useState<string | null>(null);
  const isAnalysisInProgress = sessionStatus === 'pending' || sessionStatus === 'processing';
  const loadInFlightRef = React.useRef(false);
  const lapDataRef = React.useRef<LapData | null>(null);
  React.useEffect(() => { lapDataRef.current = lapData; }, [lapData]);

  // Full-lap mode: no analysis data, animate the entire lap
  const isFullLapMode = React.useMemo(() => lapData !== null && !lapData.time_lost, [lapData]);
  const fullLapSection = React.useMemo((): SectionAnalysis | null => {
    if (!lapData || !isFullLapMode || lapData.displacement.length === 0) return null;
    return {
      start: lapData.displacement[0],
      end: lapData.displacement[lapData.displacement.length - 1],
      time_lost: 0,
      description: '',
    };
  }, [lapData, isFullLapMode]);
  const activeSection = clickedSection || fullLapSection;
  const [hoveredSection, setHoveredSection] = useState<SectionAnalysis | null>(null);
  const [convertGPSToPixel, setConvertGPSToPixel] = useState<((lat: number, lng: number) => { x: number; y: number }) | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isOriginalStateCaptured, setIsOriginalStateCaptured] = useState(false);
  const [isMapTransitioning, setIsMapTransitioning] = useState(false);
  const [mapDimensions, setMapDimensions] = useState<MapDimensions>(DEFAULT_MAP_DIMENSIONS);
  const [initialDimensionsCalculated, setInitialDimensionsCalculated] = useState(false);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const mapContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [showOptimalTimePopup, setShowOptimalTimePopup] = useState(false);
  
  // Map section onboarding
  const { showClickHints, markSectionClicked } = useMapOnboarding();
  
  // Mark onboarding as complete whenever a section is opened
  useEffect(() => {
    if (clickedSection) {
      markSectionClicked();
    }
  }, [clickedSection, markSectionClicked]);
  
  // Animation state - centralized timing control
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [animationStarted, setAnimationStarted] = useState(false); // Track if delay has passed
  const [trailResetKey, setTrailResetKey] = useState(0); // Force trail reset
  const [isInitialClick, setIsInitialClick] = useState(false); // Track if this is initial section click vs manual play
  const [pausedFrame, setPausedFrame] = useState<number | null>(null); // Store frame when paused
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0); // Playback speed multiplier
  const [isSliderDragging, setIsSliderDragging] = useState(false); // Track if user is dragging slider
  const startTimeRef = React.useRef<number | null>(null);
  const delayTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const animationIdRef = React.useRef<number | null>(null);
  const lastFrameRef = React.useRef<number>(0);
  const lastUpdateTimeRef = React.useRef<number>(0);
  const zoomToSectionRef = React.useRef<((bounds: google.maps.LatLngBounds) => Promise<void>) | null>(null);
  const resetToOriginalViewRef = React.useRef<(() => void) | null>(null);


  const calculateSectionBounds = React.useCallback((section: SectionAnalysis, lapData: LapData, clickX: number) => {
    if (!section || !lapData?.latitude?.length || !lapData?.longitude?.length) {
      throw new Error('Invalid section or lap data for bounds calculation');
    }

    const { startIndex, endIndex } = getPathSegmentIndices(
      section.start, 
      section.end, 
      lapData.displacement || []
    );
    
    // Validate indices
    if (startIndex < 0 || endIndex >= lapData.latitude.length || startIndex > endIndex) {
      throw new Error(`Invalid section indices: ${startIndex}-${endIndex}`);
    }
    
    // Get the actual section bounds
    const sectionBounds = new google.maps.LatLngBounds();
    for (let i = startIndex; i <= endIndex; i++) {
      if (i < lapData.latitude.length && i < lapData.longitude.length) {
        sectionBounds.extend(new google.maps.LatLng(lapData.latitude[i], lapData.longitude[i]));
      }
    }
    
    // If no valid bounds, return a fallback
    if (sectionBounds.isEmpty()) {
      return new google.maps.LatLngBounds(
        new google.maps.LatLng(FALLBACK_GPS_BOUNDS.SW.lat, FALLBACK_GPS_BOUNDS.SW.lng),
        new google.maps.LatLng(FALLBACK_GPS_BOUNDS.NE.lat, FALLBACK_GPS_BOUNDS.NE.lng)
      );
    }
    
    const ne = sectionBounds.getNorthEast();
    const sw = sectionBounds.getSouthWest();
    
    // Calculate section dimensions
    const latSpan = ne.lat() - sw.lat();
    const lngSpan = Math.abs(ne.lng() - sw.lng());
    
    // Calculate padding - ensure minimum padding to prevent excessive zoom
    // Desktop: 90% viewport (5.56% padding each side)
    // Mobile: 95% viewport (2.63% padding each side) 
    const viewportFill = isPortrait ? VIEWPORT_FILL_RATIOS.PORTRAIT : VIEWPORT_FILL_RATIOS.LANDSCAPE;
    const paddingFactor = (1 / viewportFill - 1) / 2;
    
    // Use minimum padding to prevent excessive zoom on small sections
    const latPadding = Math.max(latSpan * paddingFactor, MIN_GPS_PADDING);
    const lngPadding = Math.max(lngSpan * paddingFactor, MIN_GPS_PADDING);
    
    // Create bounds with symmetric padding
    const finalBounds = new google.maps.LatLngBounds();
    finalBounds.extend(new google.maps.LatLng(
      sw.lat() - latPadding, 
      sw.lng() - lngPadding
    ));
    finalBounds.extend(new google.maps.LatLng(
      ne.lat() + latPadding, 
      ne.lng() + lngPadding
    ));
    
    
    return finalBounds;
  }, [isPortrait]);

  // Helper function to handle section click logic
  const handleSectionClick = React.useCallback((section: SectionAnalysis, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Validation checks
    if (!isOriginalStateCaptured || !lapData || !section) {
      return;
    }
    
    try {
      // Get click position for layout determination
      const svgRect = e.currentTarget.closest('svg')?.getBoundingClientRect();
      const clickX = svgRect ? e.clientX - svgRect.left : DEFAULT_CLICK_X;
      
      // 1. Set up section and animation state immediately
      setClickedSection(section);
      setCurrentFrame(0);
      setPausedFrame(null); // Clear any previous pause state
      startTimeRef.current = null;
      setIsInitialClick(true); // Mark as initial click for delay
      
      // 2. Start animation immediately
      setIsAnimating(true);
      setIsPlaying(true);
      
      // 3. Zoom to section in background (don't wait)
      if (zoomToSectionRef.current) {
        const sectionBounds = calculateSectionBounds(section, lapData, clickX);
        zoomToSectionRef.current(sectionBounds).catch((error) => {
          console.warn('Failed to zoom to section:', error);
        });
      }
    } catch (error) {
      console.error('Error handling section click:', error);
    }
  }, [isOriginalStateCaptured, lapData, zoomToSectionRef, calculateSectionBounds]);


  const loadLapData = React.useCallback(async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      // Wait for auth to be ready
      if (authLoading) {
        return;
      }

      if (!isAuthenticated) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      // Coalesce: skip if a previous load is still in flight
      if (loadInFlightRef.current) return;
      loadInFlightRef.current = true;

      // Polls (we already have lapData) treat failures as transient — log and skip,
      // never overwrite a working UI with a "Back to Dashboard" failure screen.
      const isPoll = lapDataRef.current !== null;

      try {
        // Fetch session to get status
        const token = await getToken?.();
        const sessionResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/sessions/${sessionId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!sessionResponse.ok) {
          throw new Error('Failed to fetch session data');
        }
        const sessionData = await sessionResponse.json();
        setSessionStatus(sessionData.latest_job_status ?? null);

        let analysisData;

        if (isAnalyzedLap && sessionData.has_analysis && sessionData.latest_job_status === 'completed' && sessionData.latest_job_id) {
          // Analyzed lap: fetch from analysis endpoint
          const analysisId = sessionData.latest_job_id;
          analysisData = getCachedAnalysis(analysisId);
          if (!analysisData) {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/analysis/${analysisId}`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              }
            );
            if (!response.ok) {
              // Race: status flipped to completed but analysis row not yet readable.
              // Bail; the next poll tick will retry. Don't surface as error.
              if (isPoll) {
                console.warn('Analysis fetch returned non-OK during poll; will retry.');
                return;
              }
              throw new Error('Failed to fetch analysis data');
            }
            analysisData = await response.json();
          }
        } else {
          // Non-analyzed lap or no analysis: fetch raw preprocessed telemetry
          const lapsCacheKey = `session:${sessionId}`;
          analysisData = getCachedAnalysis(lapsCacheKey);
          if (!analysisData) {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/sessions/${sessionId}/laps`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              }
            );
            if (!response.ok) {
              if (isPoll) {
                console.warn('Lap fetch returned non-OK during poll; will retry.');
                return;
              }
              throw new Error('Failed to fetch session lap data');
            }
            analysisData = await response.json();
            setCachedAnalysis(lapsCacheKey, analysisData);
          }
        }

        if (!analysisData) {
          if (isPoll) return;
          setError('Failed to load analysis data');
          setLoading(false);
          return;
        }

        // Use the session result data that's already loaded
        const sessionResult = analysisData.session_result || analysisData;
        const laps = sessionResult?.lap_results || [];

        // Find the specific lap by number, or fall back to first lap with GPS
        const targetLapNumber = lapNumber ? parseInt(lapNumber) : null;
        let lapWithGPS;

        const findLapWithGPS = (lapList: any[], lapNum?: number | null) => {
          if (lapNum) {
            const match = lapList.find((lap: any) =>
              lap.lap_number === lapNum &&
              lap.position?.latitude && lap.position?.longitude &&
              lap.position.latitude.length > 0 && lap.position.longitude.length > 0
            );
            if (match) return match;
          }
          return lapList.find((lap: any) =>
            lap.position?.latitude && lap.position?.longitude &&
            lap.position.latitude.length > 0 && lap.position.longitude.length > 0
          ) || null;
        };

        lapWithGPS = findLapWithGPS(laps, targetLapNumber);

        if (!lapWithGPS) {
          if (isPoll) return;
          setError('No GPS data available for visualization');
          setLoading(false);
          return;
        }

        // Use session lap times as the authoritative source
        const sessionLapTimes: number[] = sessionData.lap_times || [];
        const bestLapTime = sessionData.best_lap_time ?? null;
        const lapTime = sessionLapTimes[lapWithGPS.lap_number - 1] ?? lapWithGPS.actual.lap_time;

        // Format the lap data exactly like AnalysisResults does
        const formattedLapData = {
          ...lapWithGPS,
          analysis: lapWithGPS.analysis,
          latitude: lapWithGPS.position.latitude,
          longitude: lapWithGPS.position.longitude,
          displacement: lapWithGPS.position.displacement,
          lap_time: lapTime,
          optimal_lap_time: lapWithGPS.optimal?.lap_time ?? null,
          time: lapWithGPS.actual.time,
          lat_accel: lapWithGPS.actual.lat_accel,
          long_accel: lapWithGPS.actual.long_accel,
          optimal_lat_accel: lapWithGPS.optimal?.lat_accel ?? null,
          optimal_long_accel: lapWithGPS.optimal?.long_accel ?? null,
          speed: lapWithGPS.actual.speed,
          optimal_speed: lapWithGPS.optimal?.speed ?? null,
          optimal_time: lapWithGPS.optimal?.time ?? null,
          time_lost: lapWithGPS.convenience?.time_lost ?? null,
          speed_delta: lapWithGPS.convenience?.speed_delta ?? null,
          best_lap_time: bestLapTime,
          filename: analysisData.filename || 'Unknown'
        };

        setLapData(formattedLapData);
        setLoading(false);
      } catch (err) {
        console.error('Error loading lap data:', err);
        if (!isPoll) {
          setError('Failed to load lap data');
          setLoading(false);
        }
      } finally {
        loadInFlightRef.current = false;
      }
  }, [sessionId, getCachedAnalysis, setCachedAnalysis, getToken, authLoading, isAuthenticated, lapNumber, isAnalyzedLap]);

  useEffect(() => {
    loadLapData();
  }, [loadLapData]);

  // Poll session status while analysis is in progress; refetch on completion.
  useEffect(() => {
    if (!isAnalysisInProgress) return;
    const interval = setInterval(() => {
      loadLapData();
    }, 3000);
    return () => clearInterval(interval);
  }, [isAnalysisInProgress, loadLapData]);

  useEffect(() => {
    if (!isPlaying || !isAnimating || !activeSection) {
      setAnimationStarted(false);
      return;
    }

    if (delayTimeoutRef.current) {
      clearTimeout(delayTimeoutRef.current);
    }

    if (isInitialClick && !isFullLapMode) {
      delayTimeoutRef.current = setTimeout(() => {
        setAnimationStarted(true);
        setIsInitialClick(false);
      }, INITIAL_ANIMATION_DELAY);
    } else {
      setAnimationStarted(true);
    }

    return () => {
      if (delayTimeoutRef.current) {
        clearTimeout(delayTimeoutRef.current);
      }
    };
  }, [isPlaying, isAnimating, activeSection, isInitialClick, isFullLapMode]);

  useEffect(() => {
    if (!animationStarted || !isPlaying || !activeSection) {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      return;
    }

    if (!lapData) return;

    const { startIndex, endIndex } = getPathSegmentIndices(
      activeSection.start,
      activeSection.end,
      lapData.displacement || []
    );
    
    const animationTiming = calculateSectionTiming(startIndex, endIndex, lapData);
    
    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }
      
      const elapsed = timestamp - startTimeRef.current;
      const adjustedDuration = animationTiming.maxSectionDuration / playbackSpeed;
      const progress = elapsed / adjustedDuration;
      const loopedProgress = progress % 1.0;
      const frameValue = Math.min(loopedProgress * 100, 99.99);
      
      // Update at 20fps, skip if user is dragging slider
      const timeSinceLastUpdate = timestamp - lastUpdateTimeRef.current;
      if (timeSinceLastUpdate >= VISUAL_UPDATE_INTERVAL_MS && !isSliderDragging) {
        lastFrameRef.current = frameValue;
        lastUpdateTimeRef.current = timestamp;
        setCurrentFrame(frameValue);
      }
      
      if (isPlaying && animationStarted) {
        animationIdRef.current = requestAnimationFrame(animate);
      } else {
        setAnimationStarted(false);
      }
    };
    
    animationIdRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    };
  }, [animationStarted, isPlaying, activeSection, lapData, playbackSpeed, isSliderDragging]);

  // In full-lap mode, show the animation panel immediately on load
  useEffect(() => {
    if (isFullLapMode) {
      setIsAnimating(true);
    }
  }, [isFullLapMode]);

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!lapData || initialDimensionsCalculated) return;
    
    const el = mapContainerRef.current;
    if (!el) return;

    const timeoutId = setTimeout(() => {
      const rect = el.getBoundingClientRect();
      const containerWidth = Math.max(0, Math.floor(rect.width));
      const containerHeight = Math.max(0, Math.floor(rect.height));
      
      if (containerWidth > 0 && containerHeight > 0) {
        // Use the full container dimensions - GoogleMapOverlay will fill 100% of its container
        // No need to subtract padding since the container itself handles sizing via flexbox
        let mapWidth = containerWidth;
        let mapHeight = containerHeight;
        
        // Round to nearest 10 for cleaner dimensions
        mapWidth = Math.round(mapWidth / 10) * 10;
        mapHeight = Math.round(mapHeight / 10) * 10;
        
        setMapDimensions({ width: mapWidth, height: mapHeight });
        setInitialDimensionsCalculated(true);
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [lapData, initialDimensionsCalculated]);

  const handleClose = React.useCallback(() => {
    // Check if mobile (window width < 768px)
    const isMobile = window.innerWidth < 768;
    
    // Clean up any running animations
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
    if (delayTimeoutRef.current) {
      clearTimeout(delayTimeoutRef.current);
      delayTimeoutRef.current = null;
    }
    
    // Navigate back to session page with state to control sidebar
    navigate(`/dashboard/session/${sessionId}`, {
      state: {
        closeSidebarOnMobile: isMobile,
        keepAnalysisSelected: true 
      }
    });
  }, [navigate, sessionId]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        padding: '2rem'
      }}>
        <div style={{ maxWidth: '600px', width: '100%', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.5rem', color: '#f3f4f6' }}>
            Loading Lap Visualization
          </h2>
          <p style={{ fontSize: '1rem', color: '#9ca3af' }}>
            This may take a few moments
          </p>
        </div>
      </div>
    );
  }

  if (error || !lapData) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#0f172a',
        color: '#f8fafc'
      }}>
        <div style={{ marginBottom: '20px' }}>
          {error || 'No lap data available'}
        </div>
        <button
          onClick={handleClose}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6366f1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard" style={{ width: '100vw', height: '100vh', margin: 0, padding: 0, overflow: 'hidden' }}>
      <CloseButton 
        onClick={handleClose} 
        className="lap-close-button"
        ariaLabel="Close lap view"
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          zIndex: 1000
        }}
      />

      <div className="lap-page-content" style={{ 
        width: '100vw', 
        height: '100vh',
        margin: 0, 
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header - natural height */}
        <div style={{
          backgroundColor: 'var(--surface)',
          borderBottom: '1px solid var(--surface-selected)',
          padding: '0.75rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          flexShrink: 0
        }}>
                {(() => {
                  const deltaBestLap = lapData.best_lap_time != null ? lapData.lap_time - lapData.best_lap_time : null;
                  const deltaOptimal = lapData.optimal_lap_time != null ? lapData.lap_time - lapData.optimal_lap_time : null;
                  const labelStyle = { color: '#9ca3af', fontSize: '1rem' } as const;
                  const valueStyle = { fontSize: '1rem', fontWeight: 'bold' } as const;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ ...valueStyle, color: '#f3f4f6' }}>Lap {lapData.lap_number}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={labelStyle}>Time:</span>
                        <span style={{ ...valueStyle, color: '#f3f4f6' }}>{formatTime(lapData.lap_time)}</span>
                      </div>
                      {deltaBestLap != null && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={labelStyle}>Delta Best Lap:</span>
                          <span style={{ ...valueStyle, color: getDeltaBestLapColor(deltaBestLap) }}>
                            {formatTimeDifference(deltaBestLap)}
                          </span>
                        </div>
                      )}
                      {deltaOptimal != null && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={labelStyle}>Delta Optimal:</span>
                          <span style={{ ...valueStyle, color: getDeltaOptimalColor(deltaOptimal) }}>
                            {formatTimeDifference(deltaOptimal)}
                          </span>
                        </div>
                      )}
                      {lapData.optimal_lap_time != null && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <button
                            onClick={() => setShowOptimalTimePopup(true)}
                            style={{
                              background: 'none', border: 'none', color: 'inherit',
                              fontSize: 'inherit', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                              padding: 0, margin: 0
                            }}
                            aria-label="Learn about optimal time calculation"
                          >
                            <span style={labelStyle}>Optimalⁱ:</span>
                          </button>
                          <span style={{ ...valueStyle, color: '#6366f1' }}>
                            {formatTime(lapData.optimal_lap_time)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
        </div>

        {/* Content area - fills remaining space */}
        <div style={{
          display: 'flex',
          flexDirection: isPortrait ? 'column' : 'row',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden'
        }}>
          <div ref={mapContainerRef} style={{
            flex: isAnimating ? (isPortrait ? '1 1 65%' : '1 1 70%') : '1 1 100%',
            position: 'relative',
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden'
          }}>
          {isAnalysisInProgress && (
            <div style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.375rem 0.75rem',
              borderRadius: '9999px',
              backgroundColor: 'rgba(17, 24, 39, 0.85)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(99, 102, 241, 0.5)',
              color: '#f3f4f6',
              fontSize: '0.8125rem',
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              pointerEvents: 'none',
            }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid rgba(99, 102, 241, 0.3)',
                  borderTopColor: '#6366f1',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <span>Analysis in progress…</span>
            </div>
          )}
          {initialDimensionsCalculated && (
            <GoogleMapOverlay
              latitude={lapData.latitude}
              longitude={lapData.longitude}
              width={mapDimensions.width}
              height={mapDimensions.height}
            onMapReady={(convertGPSToPixel) => {
              setConvertGPSToPixel(() => convertGPSToPixel);
              setIsMapReady(true);
            }}
            onMapClick={() => {
              if (isFullLapMode) return; // Don't reset animation panel in full-lap mode
              setClickedSection(null);
              setIsPlaying(false);
              setIsAnimating(false);
              setCurrentFrame(0);
              setPausedFrame(null);
              startTimeRef.current = null;
              if (resetToOriginalViewRef.current) {
                resetToOriginalViewRef.current();
              }
            }}
            onZoomToSection={(zoomFunction) => {
              zoomToSectionRef.current = zoomFunction;
            }}
            onResetToOriginalView={(resetFunction) => {
              resetToOriginalViewRef.current = resetFunction;
            }}
            onOriginalStateCaptured={() => {
              setIsOriginalStateCaptured(true);
            }}
            onActualDimensions={(actualWidth, actualHeight) => {
              // Update map dimensions to match actual Google Maps container
              if (actualWidth !== mapDimensions.width || actualHeight !== mapDimensions.height) {
                setMapDimensions({ width: actualWidth, height: actualHeight });
              }
            }}
            onMapTransitionStart={() => {
              setIsMapTransitioning(true);
            }}
            onMapTransitionEnd={() => {
              setIsMapTransitioning(false);
            }}
            >
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${mapDimensions.width} ${mapDimensions.height}`}
              style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                pointerEvents: 'none',
                opacity: isMapTransitioning ? 0 : 1,
                transition: 'opacity 0.2s ease-in-out'
              }}
            >
              <defs>
                <linearGradient id="timeLostGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgb(34, 197, 94)" />
                  <stop offset="25%" stopColor="rgb(144, 255, 94)" />
                  <stop offset="50%" stopColor="rgb(255, 255, 94)" />
                  <stop offset="75%" stopColor="rgb(255, 127, 47)" />
                  <stop offset="100%" stopColor="rgb(255, 0, 0)" />
                </linearGradient>
              </defs>
              {(() => {
                // Always use Google Maps coordinate system - no fallback to old SVG system
                const coordinates = convertGPSToPixel && isMapReady
                  ? lapData.latitude.map((lat: number, i: number) => 
                      convertGPSToPixel(lat, lapData.longitude[i])
                    )
                  : []; // Return empty array if not ready instead of using old coordinate system
                
                const hasTimeLost = lapData.time_lost != null && lapData.time_lost.length > 0;
                const maxTimeLost = hasTimeLost ? Math.max(...lapData.time_lost!) : 0;
                const maxSpeed = !hasTimeLost && lapData.speed.length > 0 ? Math.max(...lapData.speed) : 1;

                const pathElements = coordinates.slice(0, -1).map((coord: { x: number; y: number }, i: number) => {
                  const nextCoord = coordinates[i + 1];
                  // Color by time_lost if available, otherwise color by speed (green=fast, red=slow)
                  const color = hasTimeLost
                    ? getColorForTimeLost(lapData.time_lost![i] || 0, maxTimeLost)
                    : getColorForTimeLost(maxSpeed - (lapData.speed[i] || 0), maxSpeed);
                  
                  return (
                    <line
                      key={`path-${i}`}
                      x1={coord.x}
                      y1={coord.y}
                      x2={nextCoord.x}
                      y2={nextCoord.y}
                      stroke={color}
                      strokeWidth="3"
                      strokeLinecap="butt"
                      strokeLinejoin="round"
                    />
                  );
                });

                const getTimeLostColor = (timeLost: number, allTimeLosts: number[]) => {
                  const maxTimeLost = Math.max(...allTimeLosts);
                  const minTimeLost = Math.min(...allTimeLosts);
                  const range = maxTimeLost - minTimeLost;
                  
                  // If all sections have same time lost, use middle of color range
                  if (range === 0) return { bubble: '#f59e0b', section: '#f59e0b' }; // Amber
                  
                  // Normalize to 0-1 scale
                  const t = (timeLost - minTimeLost) / range;
                  
                  // Color interpolation: Yellow/Amber (#f59e0b) → Red (#dc2626)
                  // Start color (min time lost): Amber RGB(245, 158, 11)
                  const startR = 245, startG = 158, startB = 11;
                  // End color (max time lost): Red RGB(220, 38, 38)
                  const endR = 220, endG = 38, endB = 38;
                  
                  // Linear interpolation for each RGB component
                  const r = Math.round(startR + (endR - startR) * t);
                  const g = Math.round(startG + (endG - startG) * t);
                  const b = Math.round(startB + (endB - startB) * t);
                  
                  const color = `rgb(${r}, ${g}, ${b})`;
                  return { bubble: color, section: color };
                };

                const allTimeLosts = lapData.analysis?.section_analysis?.map((s: SectionAnalysis) => s.time_lost) || [];
                const sectionElements = lapData.analysis?.section_analysis?.map((section: SectionAnalysis, index: number) => {
                  const { startIndex, endIndex } = getPathSegmentIndices(
                    section.start, 
                    section.end, 
                    lapData.displacement || []
                  );
                  
                  if (startIndex >= coordinates.length || endIndex >= coordinates.length) return null;

                  const sectionColors = getTimeLostColor(section.time_lost, allTimeLosts);

                  const sectionSegments = [];
                  for (let i = startIndex; i < Math.min(endIndex, coordinates.length - 1); i++) {
                    const coord = coordinates[i];
                    const nextCoord = coordinates[i + 1];
                    
                    sectionSegments.push(
                      <line
                        key={`section-${index}-segment-${i}`}
                        x1={coord.x}
                        y1={coord.y}
                        x2={nextCoord.x}
                        y2={nextCoord.y}
                        stroke={sectionColors.section}
                        strokeOpacity="0.5"
                        strokeWidth="15"
                        strokeLinecap="butt"
                        strokeLinejoin="round"
                        onMouseEnter={() => setHoveredSection(section)}
                        onMouseLeave={() => setHoveredSection(null)}
                        style={{ cursor: 'pointer', pointerEvents: 'auto', touchAction: 'none' }}
                        onClick={(e) => handleSectionClick(section, e)}/>
                    );
                  }

                  return (
                    <g key={`section-${index}`}>
                      {sectionSegments}
                    </g>
                  );
                }) || [];

                const hoverElements = hoveredSection ? (() => {
                  const { startIndex, endIndex } = getPathSegmentIndices(
                    hoveredSection.start, 
                    hoveredSection.end, 
                    lapData.displacement || []
                  );
                  
                  const sectionColors = getTimeLostColor(hoveredSection.time_lost, allTimeLosts);
                  
                  const hoverSegments = [];
                  for (let i = startIndex; i < Math.min(endIndex, coordinates.length - 1); i++) {
                    const coord = coordinates[i];
                    const nextCoord = coordinates[i + 1];
                    
                    hoverSegments.push(
                      <line
                        key={`hover-${i}`}
                        x1={coord.x}
                        y1={coord.y}
                        x2={nextCoord.x}
                        y2={nextCoord.y}
                        stroke={sectionColors.section}
                        strokeOpacity="0.7"
                        strokeWidth="8"
                        strokeLinecap="butt"
                        strokeLinejoin="round"
                        style={{ pointerEvents: 'none' }}
                      />
                    );
                  }
                  return hoverSegments;
                })() : [];

                const sectionMarkers = lapData.analysis?.section_analysis?.map((section: SectionAnalysis, index: number) => {
                  const { startIndex, endIndex } = getPathSegmentIndices(
                    section.start, 
                    section.end, 
                    lapData.displacement || []
                  );
                  
                  if (startIndex >= coordinates.length || endIndex >= coordinates.length) return null;

                  const midIdx = Math.floor((startIndex + endIndex) / 2);
                  const midCoord = coordinates[midIdx];
                  const timeLost = section.time_lost;
                  const colors = getTimeLostColor(timeLost, allTimeLosts);
                  
                  const bubbleOffsetY = -35;
                  const bubbleHeight = showClickHints ? 36 : 24; // Taller for two lines
                  const bubbleWidth = showClickHints ? 68 : 50; // Adjusted for smaller "Click Here" font

                  // Calculate orthogonal line for section markers
                  const getOrthogonalLine = (coordIndex: number) => {
                    if (coordIndex < 1 || coordIndex >= coordinates.length - 1) return null;
                    
                    const prevCoord = coordinates[coordIndex - 1];
                    const nextCoord = coordinates[coordIndex + 1];
                    const currentCoord = coordinates[coordIndex];
                    
                    // Calculate path direction
                    const dx = nextCoord.x - prevCoord.x;
                    const dy = nextCoord.y - prevCoord.y;
                    const length = Math.sqrt(dx * dx + dy * dy);
                    
                    if (length === 0) return null;
                    
                    // Orthogonal vector (perpendicular)
                    const orthX = -dy / length;
                    const orthY = dx / length;
                    
                    const lineLength = 12;
                    return {
                      x1: currentCoord.x - orthX * lineLength,
                      y1: currentCoord.y - orthY * lineLength,
                      x2: currentCoord.x + orthX * lineLength,
                      y2: currentCoord.y + orthY * lineLength
                    };
                  };

                  const startLine = getOrthogonalLine(startIndex);
                  const endLine = getOrthogonalLine(endIndex);

                  return (
                    <g key={`markers-${index}`}>
                      {/* Start marker */}
                      {startLine && (
                        <line
                          x1={startLine.x1}
                          y1={startLine.y1}
                          x2={startLine.x2}
                          y2={startLine.y2}
                          stroke={colors.bubble}
                          strokeWidth="3"
                          strokeLinecap="round"
                          style={{ pointerEvents: 'none' }}
                        />
                      )}
                      
                      {/* End marker */}
                      {endLine && (
                        <line
                          x1={endLine.x1}
                          y1={endLine.y1}
                          x2={endLine.x2}
                          y2={endLine.y2}
                          stroke={colors.bubble}
                          strokeWidth="3"
                          strokeLinecap="round"
                          style={{ pointerEvents: 'none' }}
                        />
                      )}
                      <rect
                        x={midCoord.x - bubbleWidth/2}
                        y={midCoord.y + bubbleOffsetY - bubbleHeight/2}
                        width={bubbleWidth}
                        height={bubbleHeight}
                        fill="rgba(255, 255, 255, 0.9)"
                        stroke="rgba(0, 0, 0, 0.3)"
                        strokeWidth="1"
                        rx="12"
                      />
                      <rect
                        x={midCoord.x - (bubbleWidth-4)/2}
                        y={midCoord.y + bubbleOffsetY - (bubbleHeight-4)/2}
                        width={bubbleWidth-4}
                        height={bubbleHeight-4}
                        fill={colors.bubble}
                        fillOpacity="0.9"
                        stroke="rgba(255, 255, 255, 0.9)"
                        strokeWidth="1"
                        rx="10"
                        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        onMouseEnter={() => setHoveredSection(section)}
                        onMouseLeave={() => setHoveredSection(null)}
                        onClick={(e) => handleSectionClick(section, e)}
                      />
                      
                      <text
                        x={midCoord.x}
                        y={midCoord.y + bubbleOffsetY + 2}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="700"
                        fontFamily="monospace"
                        fill="white"
                        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        onMouseEnter={() => setHoveredSection(section)}
                        onMouseLeave={() => setHoveredSection(null)}
                        onClick={(e) => handleSectionClick(section, e)}
                      >
                        {showClickHints ? (
                          <>
                            <tspan x={midCoord.x} dy="-6">{timeLost.toFixed(1)}s</tspan>
                            <tspan x={midCoord.x} dy="12" fontSize="9">Click Here</tspan>
                          </>
                        ) : (
                          `${timeLost.toFixed(1)}s`
                        )}
                      </text>
                    </g>
                  );
                }) || [];

                const legendElements = hasTimeLost ? (
                  <TimeLostLegend
                    key="legend"
                    position="top-right"
                    containerWidth={mapDimensions.width}
                    containerHeight={mapDimensions.height}
                  />
                ) : null;

                // Start/finish marker
                let startMarker = null;
                if (convertGPSToPixel && isMapReady && lapData.latitude.length > 0) {
                  const sp = convertGPSToPixel(lapData.latitude[0], lapData.longitude[0]);
                  startMarker = (
                    <g key="start-marker">
                      <circle cx={sp.x} cy={sp.y} r="10" fill="white" stroke="#1f2937" strokeWidth="2" opacity="0.9" />
                      <circle cx={sp.x} cy={sp.y} r="5" fill="#1f2937" opacity="0.9" />
                    </g>
                  );
                }

                return [...pathElements, ...sectionElements, ...hoverElements, ...sectionMarkers, ...(legendElements ? [legendElements] : []), ...(startMarker ? [startMarker] : [])];
              })()}
            </svg>

            {activeSection && (animationStarted || pausedFrame !== null) && (() => {
              const coordinates = convertGPSToPixel && isMapReady
                ? lapData.latitude.map((lat: number, i: number) =>
                    convertGPSToPixel(lat, lapData.longitude[i])
                  )
                : [];

              const { startIndex, endIndex } = getPathSegmentIndices(
                activeSection.start,
                activeSection.end,
                lapData.displacement || []
              );
              
              if (startIndex >= coordinates.length || endIndex >= coordinates.length) return null;

              const animationTiming = calculateSectionTiming(startIndex, endIndex, lapData);
              // Use paused frame if animation is paused, otherwise use current frame
              const frameToUse = pausedFrame !== null ? pausedFrame : currentFrame;
              const currentAnimationData = getSmoothAnimationData(frameToUse, animationTiming);

              if (!currentAnimationData) return null;

              // Use GPS coordinates directly from upsampled animation data
              const actualLat = currentAnimationData.actual.latitude;
              const actualLng = currentAnimationData.actual.longitude;
              const optimalLat = currentAnimationData.optimal.latitude;
              const optimalLng = currentAnimationData.optimal.longitude;

              // Convert GPS to pixel coordinates
              const actualCoord = convertGPSToPixel ? 
                convertGPSToPixel(actualLat, actualLng) : 
                { x: 0, y: 0 };
              
              const optimalCoord = convertGPSToPixel ? 
                convertGPSToPixel(optimalLat, optimalLng) : 
                { x: 0, y: 0 };

              if (!actualCoord || !optimalCoord) return null;

              const actualSpeed = currentAnimationData.actual.speed;

              // Calculate speed difference at the same spatial position (not same time)
              const optimalSpeedAtActualPosition = currentAnimationData.actual.optimal_speed ?? null;
              const speedDifference = optimalSpeedAtActualPosition != null
                ? actualSpeed - optimalSpeedAtActualPosition
                : null;

              // Calculate time difference at the red dot's spatial position
              const sectionStartTime = animationTiming.sectionData[0]?.time ?? 0;
              const sectionStartOptimalTime = animationTiming.sectionData[0]?.optimal_time ?? 0;
              const actualSectionTime = currentAnimationData.actual.time - sectionStartTime;
              const optimalSectionTime = currentAnimationData.actual.optimal_time != null
                ? currentAnimationData.actual.optimal_time - sectionStartOptimalTime
                : null;
              const timeAhead = optimalSectionTime != null
                ? actualSectionTime - optimalSectionTime
                : null;

              return (
                <svg
                  width="100%"
                  height="100%"
                  viewBox={`0 0 ${mapDimensions.width} ${mapDimensions.height}`}
                  style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 20 }}
                >
                  {/* Render dots first */}
                  <g>
                    <circle
                      cx={actualCoord.x}
                      cy={actualCoord.y}
                      r="14"
                      fill="rgba(255, 255, 255, 0.9)"
                      stroke="rgba(0, 0, 0, 0.3)"
                      strokeWidth="1"
                    />
                    <circle
                      cx={actualCoord.x}
                      cy={actualCoord.y}
                      r="11"
                      fill={SPEED_COLORS.ACTUAL}
                      fillOpacity="0.9"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="2"
                    />
                  </g>
                  
                  {!isFullLapMode && (
                    <g>
                      <circle cx={optimalCoord.x} cy={optimalCoord.y} r="14" fill="rgba(255, 255, 255, 0.9)" stroke="rgba(0, 0, 0, 0.3)" strokeWidth="1" />
                      <circle cx={optimalCoord.x} cy={optimalCoord.y} r="11" fill={SPEED_COLORS.OPTIMAL} fillOpacity="0.9" stroke="rgba(255, 255, 255, 0.9)" strokeWidth="2" />
                    </g>
                  )}

                  {/* Speed label above actual car */}
                  <g>
                    <rect x={actualCoord.x - 40} y={actualCoord.y - 50} width="80" height={isFullLapMode ? 18 : 28} fill="rgba(0, 0, 0, 0.7)" stroke={SPEED_COLORS.ACTUAL} strokeWidth="1" rx="9" />
                    <text x={actualCoord.x} y={actualCoord.y - (isFullLapMode ? 37 : 37)} textAnchor="middle" fontSize="12" fontWeight="700" fontFamily="monospace" fill="white">
                      {actualSpeed.toFixed(0)} mph
                    </text>
                    {!isFullLapMode && (
                      <text x={actualCoord.x} y={actualCoord.y - 25} textAnchor="middle" fontSize="12" fontWeight="700" fontFamily="monospace" fill="white">
                        {speedDifference != null ? `${speedDifference >= 0 ? '+' : ''}${speedDifference.toFixed(1)} mph` : '—'}
                      </text>
                    )}
                  </g>

                  {!isFullLapMode && (
                    <g>
                      <rect x={optimalCoord.x - 25} y={optimalCoord.y + 15} width="50" height="18" fill="rgba(0, 0, 0, 0.7)" stroke={SPEED_COLORS.OPTIMAL} strokeWidth="1" rx="9" />
                      <text x={optimalCoord.x} y={optimalCoord.y + 28} textAnchor="middle" fontSize="12" fontWeight="700" fontFamily="monospace" fill="white">
                        {timeAhead != null ? `${(-timeAhead).toFixed(2)}s` : '—'}
                      </text>
                    </g>
                  )}
                </svg>
              );
            })()}

          </GoogleMapOverlay>
          )}
          
          {/* Progress slider with controls - positioned outside GoogleMapOverlay but over the map */}
          {isAnimating && (isFullLapMode || animationStarted || pausedFrame !== null) && (
            <div style={{
              position: 'absolute',
              bottom: isPortrait ? '10px' : '20px',
              left: isPortrait ? '10px' : '20px',
              right: isPortrait ? (isAnimating ? '10px' : '10px') : '20px',
              zIndex: 100,
              pointerEvents: 'none', // Let events pass through the container
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              padding: '8px 16px',
              borderRadius: '8px',
              backdropFilter: 'blur(4px)',
            }}>
              {/* Controls and slider in same row */}
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                pointerEvents: 'auto',
              }}>
                {/* Play/pause and speed controls */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  flexShrink: 0,
                }}>
                  <button
                    onClick={() => {
                    if (isPlaying) {
                      // Pause: store current frame and stop animation
                      setPausedFrame(currentFrame);
                      setIsPlaying(false);
                      if (delayTimeoutRef.current) {
                        clearTimeout(delayTimeoutRef.current);
                      }
                    } else {
                      // Resume: continue from paused frame or restart if no pause
                      if (pausedFrame !== null) {
                        // Resuming from pause - continue from where we left off
                        setCurrentFrame(pausedFrame);
                        setPausedFrame(null);
                        setIsPlaying(true);
                        setAnimationStarted(true);
                        // Reset start time to current time minus elapsed time
                        const { startIndex, endIndex } = getPathSegmentIndices(
                          activeSection!.start,
                          activeSection!.end,
                          lapData.displacement || []
                        );
                        const animationTiming = calculateSectionTiming(startIndex, endIndex, lapData);
                        const adjustedDuration = animationTiming.maxSectionDuration / playbackSpeed;
                        const elapsedTime = (pausedFrame / 100) * adjustedDuration;
                        startTimeRef.current = performance.now() - elapsedTime;
                      } else {
                        // Starting fresh - reset everything
                        setIsPlaying(true);
                        setIsAnimating(true);
                        setAnimationStarted(false);
                        setCurrentFrame(0);
                        startTimeRef.current = null;
                        setTrailResetKey(prev => prev + 1); // Reset trail when manually restarting
                      }
                    }
                  }}
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: isPlaying ? '#ef4444' : '#10b981',
                    border: '1px solid ' + (isPlaying ? '#ef4444' : '#10b981'),
                    padding: '6px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    width: '36px',
                    height: '36px',
                    minWidth: 'unset',
                    minHeight: 'unset',
                    fontWeight: 'normal',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  >
                  {isPlaying ? '⏸' : '▶'}
                </button>
                  <PlaybackSpeedSelector
                    currentSpeed={playbackSpeed}
                    onSpeedChange={(speed) => {
                      setPlaybackSpeed(speed);
                      if (isPlaying && startTimeRef.current !== null && activeSection) {
                        const { startIndex, endIndex } = getPathSegmentIndices(
                          activeSection.start,
                          activeSection.end,
                          lapData.displacement || []
                        );
                        const animationTiming = calculateSectionTiming(startIndex, endIndex, lapData);
                        const currentProgress = currentFrame / 100;
                        const adjustedDuration = animationTiming.maxSectionDuration / speed;
                        const elapsedTime = currentProgress * adjustedDuration;
                        startTimeRef.current = performance.now() - elapsedTime;
                      }
                    }}
                  />
                </div>
                
                {/* Progress slider - takes remaining space */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <AnimationProgressSlider
                currentFrame={pausedFrame !== null ? pausedFrame : currentFrame}
                onDragStart={() => setIsSliderDragging(true)}
                onDragEnd={() => setIsSliderDragging(false)}
                onFrameChange={(newFrame) => {
                  // Update the frame without changing play/pause state
                  // Always update current frame for visual feedback
                  setCurrentFrame(newFrame);
                  
                  if (pausedFrame !== null) {
                    setPausedFrame(newFrame);
                  } else if (startTimeRef.current !== null && activeSection) {
                    const { startIndex, endIndex } = getPathSegmentIndices(
                      activeSection.start,
                      activeSection.end,
                      lapData.displacement || []
                    );
                    const animationTiming = calculateSectionTiming(startIndex, endIndex, lapData);
                    const adjustedDuration = animationTiming.maxSectionDuration / playbackSpeed;
                    const elapsedTime = (newFrame / 100) * adjustedDuration;
                    startTimeRef.current = performance.now() - elapsedTime;
                  }
                }}
                isPlaying={isPlaying && animationStarted}
              />
                </div>
              </div>
            </div>
          )}
          </div>
          {isAnimating && (clickedSection || isFullLapMode) && (
            <div style={{
              flex: isPortrait ? '0 0 35%' : '0 0 30%',
              maxHeight: isPortrait ? '400px' : 'none',
              maxWidth: isPortrait ? 'none' : '350px',
              backgroundColor: 'var(--surface)',
              borderTop: isPortrait ? '1px solid var(--surface-selected)' : 'none',
              borderLeft: !isPortrait ? '1px solid var(--surface-selected)' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              minWidth: 0,
              minHeight: 0
            }}>
              <SectionAnalysisPopup
                section={activeSection!}
                lapData={lapData}
                isPlaying={isPlaying && animationStarted}
                currentFrame={pausedFrame !== null ? pausedFrame : currentFrame}
                resetKey={trailResetKey}
                hasOptimal={!isFullLapMode}
                onClose={() => {
                  setClickedSection(null);
                  setIsPlaying(false);
                  setIsAnimating(false);
                  setAnimationStarted(false);
                  setCurrentFrame(0);
                  setPausedFrame(null); // Clear paused state
                  if (delayTimeoutRef.current) {
                    clearTimeout(delayTimeoutRef.current);
                  }
                  // Reset map to original view
                  if (resetToOriginalViewRef.current) {
                    resetToOriginalViewRef.current();
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
      
      <OptimalTimePopup 
        isOpen={showOptimalTimePopup}
        onClose={() => setShowOptimalTimePopup(false)}
      />
    </div>
  );
};

export default LapVisualizationPage;
